import { type ReactNode, useState } from 'react';
import { Input } from '../../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Icon, type IconName } from '../icon';
import { cn } from '../utils';

export interface AccountMenuUser {
  name?: string;
  email?: string;
}

export interface AccountMenuItem {
  id: string;
  label: string;
  icon?: IconName;
  variant?: 'default' | 'danger';
  onClick: () => void;
}

export interface AccountMenuProps {
  ariaLabel: string;
  user: AccountMenuUser;
  searchPlaceholder?: string;
  items: AccountMenuItem[];
  children: ReactNode;
}

function AccountMenuIdentity({ user }: { user: AccountMenuUser }) {
  return (
    <div className="flex w-full items-start gap-2">
      <Icon name="personal-data" />
      <div className="flex min-w-0 flex-col">
        {user.name ? <span className="truncate font-body font-semibold text-base text-black">{user.name}</span> : null}
        {user.email ? <span className="truncate font-body text-sm text-subtle-foreground">{user.email}</span> : null}
      </div>
    </div>
  );
}

function AccountMenuSearch({ placeholder }: { placeholder: string }) {
  return <Input iconLeft={<Icon name="search" />} placeholder={placeholder} className="gap-0.5 pl-1 focus-within:pl-[3px]" />;
}

function AccountMenuListItem({ item, onClick }: { item: AccountMenuItem; onClick: () => void }) {
  return (
    <button
      type="button"
      className={cn(
        'flex min-h-10 w-full cursor-default items-center gap-2 text-left font-body font-semibold text-base leading-body',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        item.variant === 'danger' ? 'text-error-on-subtle' : 'text-foreground',
      )}
      onClick={onClick}
    >
      {item.icon ? <Icon name={item.icon} className="shrink-0 cursor-pointer" /> : null}
      <span className="flex-1 cursor-pointer">{item.label}</span>
      {item.variant === 'danger' ? (
        <span className="size-4 shrink-0" />
      ) : (
        <Icon name="chevron-right" size="sm" className="shrink-0 text-subtle-foreground" />
      )}
    </button>
  );
}

function AccountMenuItems({ items, onClose }: { items: AccountMenuItem[]; onClose: () => void }) {
  const dangerIndex = items.findIndex((item) => item.variant === 'danger');
  const primaryItems = dangerIndex === -1 ? items : items.slice(0, dangerIndex);
  const dangerItems = dangerIndex === -1 ? [] : items.slice(dangerIndex);

  const renderItem = (item: AccountMenuItem) => (
    <AccountMenuListItem
      key={item.id}
      item={item}
      onClick={() => {
        item.onClick();
        onClose();
      }}
    />
  );

  return (
    <div className="flex flex-col gap-2">
      {primaryItems.map(renderItem)}
      {primaryItems.length > 0 && dangerItems.length > 0 ? (
        <div className="py-2">
          <Divider />
        </div>
      ) : null}
      {dangerItems.map(renderItem)}
    </div>
  );
}

function Divider() {
  return <hr className="h-px w-full border-0 bg-border-subtle" />;
}

export function AccountMenu({ ariaLabel, user, searchPlaceholder, items, children }: AccountMenuProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={-40} // Negative offset to account for the header height
        collisionPadding={8}
        aria-label={ariaLabel}
        // Radix computes width from collisionPadding
        className="flex w-[var(--radix-popper-available-width)] flex-col overflow-hidden rounded-2xl border-0 p-4 shadow-popover sm:w-96"
      >
        <AccountMenuIdentity user={user} />
        {searchPlaceholder ? (
          <div className="pt-4">
            <AccountMenuSearch placeholder={searchPlaceholder} />
          </div>
        ) : null}
        <div className="py-4">
          <Divider />
        </div>
        <AccountMenuItems items={items} onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
